import {
	createValidationConfiguration,
	IRootValidation,
	IValidationConfiguration,
	useValidation
} from "client/Validation";
import { IObjectInputAttachable, InputAttachable, IAggregateAttachable } from "./attachable";
import { createFormReducer, IValueReducer } from "./reducer";
import {
	Dispatch,
	RefObject,
	SetStateAction,
	useMemo,
	useRef
} from "react";
import { IValidationStep } from "client/Validation/steps";

interface IFormHandler<TValue> extends Omit<IObjectInputAttachable<TValue>, "validation"> {
	readonly validation: IRootValidation<TValue>;
	forStep(step: IValidationStep): IAggregateAttachable;
}

function useFormHandler<TModel>(
	model: TModel,
	setModel: Dispatch<SetStateAction<TModel>>,
	validationConfiguration: IValidationConfiguration<TModel> | "NoValidation"
): IFormHandler<TModel> {
	const modelAccessor: RefObject<TModel> = useRef<TModel>(model);
	modelAccessor.current = model;

	const formReducer: IValueReducer<TModel> = useMemo(
		() => createFormReducer<TModel>(() => modelAccessor.current, setModel),
		[setModel]
	);

	const noValidationConfiguration: IValidationConfiguration<TModel> =
		useMemo(
			() => createValidationConfiguration<TModel>(),
			[]
		);

	if (validationConfiguration === "NoValidation") {
		validationConfiguration = noValidationConfiguration;
	}

	const modelValidation: IRootValidation<TModel> = useValidation<TModel>(model, validationConfiguration);

	const formAttachable = useMemo(
		() => new InputAttachable<unknown, any>(formReducer, modelValidation),
		[formReducer, modelValidation]
	);

	return formAttachable as unknown as IFormHandler<TModel>;
}

export {
	useFormHandler,
	type IFormHandler
};